import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import IconButton from '@mui/material/IconButton';
import { useNavigate } from 'react-router-dom';

//Utils
import { ColorsContext } from "../../../../Context/ColorsContext"
import { FormatDate } from '../../../../utils/formatDate';

import { Grid } from '@mui/material';
import { LoadingProgress } from '../../../LoadingProgress';

import {
		CustomTableContainer,
  } from './styles';
import { AddCircleOutline, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next'

// Permissions
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../../../Context/PermissionsContext';
import { Cell, CustomButtonV2, CustomSelect, CustomSelectInput, CustomTable, CustomTableHead, CustomToolbar, HeaderCell, TableContainer, TableContent } from '../../../../pages/styles';

const headCells = [
    {
      id: 'id',
      center: false,
      disablePadding: false,
      sortArrow: true,
      label: 'workflowManagement.table.number'
    },
    {
        id: 'name',
        center: false,
        disablePadding:true,
        sortArrow: true,
        label: 'workflowManagement.table.name'
    },
    {
        id: 'integrationKey',
        center: false,
        disablePadding:true,
        sortArrow: true,
        label: 'workflowManagement.table.integrationKey'
    },
    {
      id: 'lastUpdated',
      center:false,
      disablePadding: true,
      sortArrow: true,
      label: 'workflowManagement.table.lastUpdate'
    },
    {
      id: 'actions',
      center: true,
      disablePadding: true,
      sortArrow: false,
      label: 'common.actions.title'
    }
  ];

  function EnhancedTableHead(props) {
    const { order, orderBy, onRequestSort } = props;
		const { t } = useTranslation();

    const createSortHandler = (property) => (event) => {
      onRequestSort(event, property);
    };
  
    return (
      <CustomTableHead>
        <TableRow>
          {headCells.map((headCell) => (
            <HeaderCell
              key={headCell.id}
              align={headCell.center ? 'center' : 'left'}
              padding={headCell.disablePadding ? 'none' : 'normal'}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              {headCell.sortArrow == true &&
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : 'asc'}
                  onClick={createSortHandler(headCell.id)}
                >
                  {t(headCell.label) || headCell.label}
                  {orderBy === headCell.id ? (
                    <Box component="span">
                      {order === 'desc' ? '' : ''}
                    </Box>
                  ) : null}
                </TableSortLabel>
              }
              {headCell.sortArrow == false &&
                <>
                  <Box component="span">
                    {t(headCell.label) || headCell.label}
                  </Box>
                </>
              }
            </HeaderCell>
          ))}
        </TableRow>
      </CustomTableHead >
    );
  }

  EnhancedTableHead.propTypes = {
    onRequestSort: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
  };

  function EnhancedTableToolbar(props) {
		const { t } = useTranslation();
    const ability = useAbility(AbilityContext);

    return (
      <CustomToolbar sx={{ mt: 2 }}>
        {ability.can("WorkflowList") &&
				<Grid container spacing={1} columns={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 8 }}>

					<Grid item xs={8} sm={6} lg={4} xl={2}>
						<CustomSelect
							id="combo-box-demo"                   
							onChange={(_, value) => {
									props.handleSelectedWorkflow(value);
							}}
							value={props.selectedWorkflow}
							isOptionEqualToValue={(option, value) => option.id == value.id}
							getOptionLabel={(option) => option != null ? option.name : ''}
							size={'small'}
							options={props.workflows.map((obj) => {return obj})}
							sx={{ width: '100%' }}
							renderInput={(params) => <CustomSelectInput {...params} label={t('workflowManagement.selectRelation')} />}
						/>
					</Grid>
					
					<Grid item xs={4} sm={3} lg={2} xl={1}>
						<CustomButtonV2
							btnType="primary"
							startIcon={<AddCircleOutline />}
							onClick={props.handleAddRelation}
						>
							{t('workflowManagement.addRelation')}
						</CustomButtonV2>
					</Grid>

				</Grid>
        }
      </CustomToolbar>
    );
  }

EnhancedTableToolbar.propTypes = {
};

  export default function TableWorkflowRelation({
    filteredRequest,
    rows,
    loading,
    handleDelete,
    handleAddRelation,
    handleSelectedWorkflow,
    workflows,
    selectedWorkflow
  }) {
    
    const navigate = useNavigate();
		const { t } = useTranslation();
    const { colors } = React.useContext(ColorsContext);
  
    const [order, setOrder] = React.useState('asc');
    const [orderBy, setOrderBy] = React.useState('calories');
    const [page, setPage] = React.useState(0);
    const [dense,] = React.useState(true);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
  
    const handleRequestSort = (event, property) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    };
    const {format} = new FormatDate();
  
  
    const handleChangePage = (event, newPage) => {
      setPage(newPage);
    };
  
    const handleChangeRowsPerPage = (event) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    };
  
    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
      page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;
  
    return (
      <Box sx={{ width: '100%' }}>
				<EnhancedTableToolbar
					workflows={workflows}
					handleAddRelation={handleAddRelation}
					selectedWorkflow={selectedWorkflow}
					handleSelectedWorkflow={handleSelectedWorkflow}
				/>
				{loading && <LoadingProgress/>}
				{!loading && 
					<TableContainer>
						<CustomTableContainer>
							<CustomTable
								sx={{ minWidth: 750 }}
								aria-labelledby="tableTitle"
								size={dense ? 'small' : 'medium'}
							>
								<EnhancedTableHead
									order={order}
									orderBy={orderBy}
									onRequestSort={handleRequestSort}
								/>
								<TableContent>
									{
										rows.map((row, index) => {

											return (
												<TableRow
													hover
													role="checkbox"
													tabIndex={-1}
													key={row.id}
												>
													<Cell>{row.id}</Cell>
													<Cell component="th" scope="row">
														{row.name}
													</Cell>
													<Cell>
															{row.integrationKey}
													</Cell>
													<Cell>
														{format({ date: row.updatedAt, format: true })}
													</Cell>
													<Cell>
														<Grid container spacing={1}>
																	<Grid item>   
																	{/* boton delete  */}
																		<IconButton
																			size="small"
																			sx={{
																				borderRadius: '10%',
																				background: colors.background,
																				color: colors.buttonActionColor
																			}}
																			aria-label="delete"
																			onClick={() => handleDelete(row, index)}
																		>
																			<Delete/>
																		</IconButton>
																	</Grid>
														</Grid>
													</Cell>
												</TableRow>
											);
										})}
									{emptyRows > 0 && (
										<TableRow
											style={{
												height: (dense ? 33 : 53) * emptyRows,
											}}
										>
											<Cell colSpan={6} />
										</TableRow>
									)}
								</TableContent>
							</CustomTable>
						</CustomTableContainer>
						
						<TablePagination
							rowsPerPageOptions={[5, 10, 25]}
							component="div"
							count={rows.length}
							rowsPerPage={rowsPerPage}
							page={page}
							onPageChange={handleChangePage}
							onRowsPerPageChange={handleChangeRowsPerPage}
							labelRowsPerPage={t('common.itemsPerPage')}
							labelDisplayedRows={
								({ from, to, count }) => {
									return '' + from + '-' + to + ` ${t('common.of')} ` + count
								}
							}
							sx={{
								borderTop: '1px solid ' + colors.gray,
							}}
						/>
					</TableContainer>
				}
      </Box>
    );
  }